/**
 * CDN DIT URL'lerinden responsive srcset üretir.
 * Manşet teslimatı: 1920px'e kadar responsive, en-boy oranı sabit.
 */
const DIT_RE =
  /^(https?:\/\/[^/]+)\/(\d+)x(\d+)\/(\d+)\/(cover|inside)\/(.+)$/i

export function rewriteDitUrl(
  url: string,
  width: number,
  height: number,
  quality: number,
): string {
  const m = String(url || '').match(DIT_RE)
  if (!m) return url
  const [, origin, , , , fit, rest] = m
  return `${origin}/${width}x${height}/${quality}/${fit}/${rest}`
}

/** Fill candidate gaps without increasing compression or changing the source crop. */
export const RESPONSIVE_IMAGE_WIDTHS = [320, 384, 480, 640, 720, 768, 828, 960, 1080, 1200, 1280, 1440, 1600, 1800, 1920] as const

/** Object-cover needs enough pixels for the cropped height as well as its frame width. */
export function gridMastheadImageSizes(url: string, isHome: boolean, side = false): string {
  const match = String(url || '').match(DIT_RE)
  const ratio = match && Number(match[2]) > 0 && Number(match[3]) > 0 ? Number(match[2]) / Number(match[3]) : 9 / 5
  const cover = (width: string, height: number) => `max(${width}, ${(height * ratio).toFixed(2)}px)`
  const mobileHeight = side ? (isHome ? 140 : 168) : (isHome ? 200 : 260)
  const tabletHeight = side ? (isHome ? 150 : 184) : (isHome ? 220 : 300)
  const desktopHeight = side ? (isHome ? 134 : 246) : (isHome ? 288 : 512)
  const mobile = cover('100vw - 1rem', mobileHeight)
  const smallTablet = side ? '(100vw - 3rem) / 2' : '100vw - 2rem'
  const tablet = side ? '(100vw - 3.5rem) / 2' : '100vw - 2.5rem'
  const desktop = `(min(100vw, 1280px) - 5rem) * ${side ? '1' : '1.2'} / 3.2`
  return `(max-width: 639px) calc(${mobile}), (max-width: 767px) calc(${cover(smallTablet, tabletHeight)}), (max-width: 1023px) calc(${cover(tablet, tabletHeight)}), calc(${cover(desktop, desktopHeight)})`
}

function sourceVariant(url: string, width: number): string {
  const match = String(url || '').match(DIT_RE)
  if (!match) return url
  const [, , sourceWidth, sourceHeight, quality] = match
  if (!Number(sourceWidth) || !Number(sourceHeight)) return url
  return rewriteDitUrl(url, width, Math.max(1, Math.round(width * Number(sourceHeight) / Number(sourceWidth))), Number(quality))
}

function mastheadVariants(url: string, widths: readonly number[]): string | undefined {
  const match = String(url || '').match(DIT_RE)
  if (!match || !Number(match[2]) || !Number(match[3])) return undefined
  return widths
    .map((width) => `${sourceVariant(url, width)} ${width}w`)
    .join(', ')
}

/** Mobil manşet: 2×/3× ekranlarda da yeterli çözünürlük. */
export function mastheadMobileSrcSet(url: string): string | undefined {
  return mastheadVariants(url, RESPONSIVE_IMAGE_WIDTHS)
}

/** Masaüstü manşet — tarayıcı konteyner ve ekran yoğunluğuna göre seçer. */
export function mastheadDesktopSrcSet(url: string): string | undefined {
  return mastheadVariants(url, RESPONSIVE_IMAGE_WIDTHS)
}

/** @deprecated — picture source'ları tercih et */
export function mastheadSrcSet(url: string): string | undefined {
  const mobile = mastheadMobileSrcSet(url)
  const desktop = mastheadDesktopSrcSet(url)
  if (!mobile || !desktop) return mobile || desktop
  return [...new Set([...mobile.split(', '), ...desktop.split(', ')])].join(', ')
}

/** Srcset desteklenmediğinde de küçük ve bulanık görsel sunma. */
export function mastheadDefaultSrc(url: string): string {
  return sourceVariant(url, 640)
}

/** Masaüstü fallback; responsive tarayıcılar ekranına uygun adayı seçer. */
export function mastheadDesktopDefaultSrc(url: string): string {
  return sourceVariant(url, 1280)
}
